import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, Upload, Brain, Zap, ShieldCheck, AlertTriangle, Check, Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo.png";
import toolpilotBadge from "@/assets/toolpilot-badge.png";
import { trackDirectoryBadgeClick } from "@/lib/directoryBadgeTracking";

import {
  matchCachedAnalysis,
  consumeFreeCredit,
  peekFreeRemaining,
  loadCache,
  getRoutingOutcome,
  getFreeState,
  shouldChargeForAnalysis,
  BUILT_IN_EXAMPLE,
  FREE_LIMIT,
} from "@/lib/freeTier";
import { trackEvent } from "@/lib/analytics";
import FreeLimitModal from "@/components/landing/FreeLimitModal";
import FallbackNoMatchCard from "@/components/landing/FallbackNoMatchCard";
import AuthModal from "@/components/AuthModal";
import SEO from "@/components/SEO";
import { ORGANIZATION_LD, SOFTWARE_APPLICATION_LD } from "@/lib/structuredData";
import { Button } from "@/components/ui/button";




/**
 * CinematicLanding — Forensic behavioral intelligence landing.
 * Cyan/ice-blue primary accents. Red reserved for detected-threat labels only.
 * Mobile-first, compressed scroll, optimized for social traffic.
 */

const FLOATING_LABELS = [
  "INVALIDATION",
  "DEFLECTION",
  "REALITY REVERSAL",
  "GASLIGHTING",
];

const PATTERNS = [
  { name: "INVALIDATION", desc: "your feelings brushed off as overreaction" },
  { name: "BLAME REDIRECTION", desc: "somehow the problem becomes you" },
  { name: "DEFLECTION", desc: "the subject changes the moment it matters" },
  { name: "FUTURE FAKING", desc: "promises that buy time and never arrive" },
  { name: "REALITY DISTORTION", desc: "events rewritten until you doubt your own memory" },
  { name: "EMOTIONAL EROSION", desc: "the slow wear-down that's hard to point to" },
];




const FloatingLabels = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    {FLOATING_LABELS.map((label, i) => {
      const top = [14, 32, 58, 74][i];
      const side = i % 2 === 0 ? "left" : "right";
      const offset = [6, 8, 10, 5][i];
      const delay = i * 1.8;
      return (
        <span
          key={label}
          className="absolute text-[10px] md:text-xs font-mono tracking-[0.3em] text-red-400/55 whitespace-nowrap select-none animate-floatLabel"
          style={{
            top: `${top}%`,
            [side]: `${offset}%`,
            animationDelay: `${delay}s`,
            textShadow: "0 0 14px rgba(239,68,68,0.3)",
          }}
        >
          {label}
        </span>
      );
    })}
  </div>
);


const Section = ({
  eyebrow,
  children,
  className,
}: {
  eyebrow?: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <section className={cn("relative px-5 py-10 md:py-12 max-w-6xl mx-auto", className)}>
    {eyebrow && (
      <div className="flex items-center gap-3 mb-5">
        <span className="h-px w-8 bg-cyan-400/50" />
        <span className="text-[10px] uppercase tracking-[0.3em] font-mono text-cyan-300/80">
          {eyebrow}
        </span>
      </div>
    )}
    {children}
  </section>
);


/* Inline paste analyzer used in hero + final CTA. Free-tier cached matcher. */
export const PasteAnalyzer = ({ size = "lg", showReassurance = true }: { size?: "lg" | "md"; showReassurance?: boolean }) => {
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [limitOpen, setLimitOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [fallback, setFallback] = useState<string | null>(null);

  const openSignup = (msg: string) => {
    try { localStorage.setItem("greyrock_pending_analysis", msg); } catch {}
    trackEvent("signup_cta_clicked", { source: "landing_paste" });
    setAuthOpen(true);
  };

  // A double-click used to burn two of the three free analyses: React state
  // hasn't flushed between two clicks in the same tick, so the old
  // `if (submitting) return` guard let both through.
  const lockRef = useRef(false);

  const submit = useCallback(async () => {
    if (lockRef.current) return;
    const typed = value.trim();
    const message = typed || BUILT_IN_EXAMPLE;
    lockRef.current = true;

    if (peekFreeRemaining() <= 0) {
      lockRef.current = false;
      trackEvent("free_limit_reached", { message_length: message.length });
      setLimitOpen(true);
      return;
    }

    setSubmitting(true);
    setFallback(null);
    trackEvent("free_analysis_started", { message_length: message.length });

    try {
      const result = await matchCachedAnalysis(message);
      trackEvent("free_analysis_routed", getRoutingOutcome(result));

      // Tier 3 (generic / true no-match): no credit consumed; route to /try
      // with the generic articulation + signup CTA (the conversion moment).
      if (result.matchType === "generic") {
        lockRef.current = false;
        navigate("/try", {
          state: {
            message,
            entry: null,
            matchType: "generic",
            family: null,
            remaining: peekFreeRemaining(),
          },
        });
        return;
      }

      // Tier 1 (exact/fuzzy) and Tier 2 (weak/variant) — consume a credit and
      // route. The built-in example is free: the visitor typed nothing.
      const after = shouldChargeForAnalysis(typed) ? consumeFreeCredit() : getFreeState();
      lockRef.current = false;
      navigate("/try", {
        state: {
          message,
          entry: result.matchType === "weak" ? null : result.entry,
          matchType: result.matchType,
          family: result.matchType === "weak" ? result.family : null,
          displayLabel: result.matchType === "weak" ? result.displayLabel : null,
          articulation: result.matchType === "weak" ? result.articulation : null,
          remaining: Math.max(0, FREE_LIMIT - after.count),
        },
      });
    } catch (err) {
      console.error("[PasteAnalyzer] match failed", err);
      setSubmitting(false);
      lockRef.current = false;
    }
  }, [value, navigate]);

  // Pre-warm cache on mount
  useEffect(() => { loadCache().catch(() => {}); }, []);

  const rows = size === "lg" ? 5 : 4;

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="relative rounded-2xl border border-cyan-400/25 bg-black/60 backdrop-blur-md shadow-[0_0_60px_-20px_rgba(34,211,238,0.55)] focus-within:border-cyan-400/60 focus-within:shadow-[0_0_80px_-15px_rgba(34,211,238,0.75)] transition-all">
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
          }}
          placeholder="Paste a text, DM, or conversation here…"
          rows={rows}
          className="w-full bg-transparent resize-none rounded-2xl px-4 py-3.5 md:px-5 md:py-4 text-[15px] md:text-[16px] leading-relaxed text-white placeholder:text-white/35 focus:outline-none font-sans"
          aria-label="Paste a message to analyze"
        />
        <div className="absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent pointer-events-none" />
      </div>
      <button
        onClick={submit}
        disabled={submitting}
        className={cn(
          "mt-3 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 h-12 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black text-sm font-semibold tracking-wide transition-all",
          "shadow-[0_0_40px_-6px_rgba(34,211,238,0.85)] hover:shadow-[0_0_60px_-4px_rgba(34,211,238,1)] animate-cyanPulse",
          "disabled:opacity-50 disabled:pointer-events-none disabled:animate-none"
        )}
      >
        <Zap className="w-4 h-4" />
        {submitting ? "Analyzing…" : "Analyze — Free"}
        <ArrowRight className="w-4 h-4" />
      </button>
      {showReassurance && (
        <div className="mt-4 space-y-1.5">
          <p className="text-[13px] text-white/60 leading-relaxed flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 text-cyan-300/70 mt-0.5 flex-shrink-0" />
            <span>Analyzed privately in your browser — your message is never sent to a server, stored, or seen by us.</span>
          </p>
          <p className="text-[11px] text-white/35 leading-relaxed pl-6">
            Free analysis runs entirely on your device. We record anonymous usage counts only — never your message.
          </p>
          <p className="text-[12px] text-white/45 leading-relaxed pl-6">
            {peekFreeRemaining()} of 3 free analyses remaining · No signup required
          </p>
        </div>
      )}

      {fallback && (
        <FallbackNoMatchCard
          message={fallback}
          onSignup={() => openSignup(fallback)}
          onDismiss={() => setFallback(null)}
        />
      )}

      <FreeLimitModal
        open={limitOpen}
        onOpenChange={setLimitOpen}
        onSignup={() => { setLimitOpen(false); openSignup(value.trim() || "You're too sensitive."); }}
      />
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} defaultMode="signup" />
    </div>
  );
};

const CinematicLanding = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);


  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);



  const goToSignin = () => {
    setMenuOpen(false);
    navigate("/login");
  };


  return (
    <div className="min-h-screen bg-black text-white antialiased overflow-x-hidden cinematic-root">
      <SEO
        title="GreyRock AI® — Finally Name What You Couldn't Explain"
        description="Paste any message. See the pattern named in seconds. Forensic clarity on manipulation, gaslighting, and coercive control. Free, private, instant."
        path="/"
        jsonLd={[ORGANIZATION_LD, SOFTWARE_APPLICATION_LD]}
      />
      {/* Ambient backdrop */}
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none -z-10"
        style={{
          background:
            "radial-gradient(120% 80% at 50% 0%, rgba(8,51,68,0.55) 0%, rgba(0,0,0,0) 55%)," +
            "linear-gradient(180deg, #050608 0%, #08090c 60%, #000 100%)",
        }}
      />
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none -z-10 opacity-[0.04] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* NAV */}
      <nav className="sticky top-0 z-30 backdrop-blur-md bg-black/60 border-b border-cyan-400/10">
        <div className="max-w-6xl mx-auto px-5 h-12 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <img
              src={logo}
              alt="GreyRock AI"
              width={28}
              height={28}
              fetchPriority="high"
              decoding="async"
              className="w-7 h-7 object-contain drop-shadow-[0_0_12px_rgba(0,242,255,0.3)] group-hover:scale-110 transition-transform"
            />
            <span className="text-sm font-semibold tracking-tight">
              GreyRock<span className="text-cyan-300">AI</span>
              <sup className="text-[11px] text-cyan-300 ml-0.5 align-super drop-shadow-[0_0_6px_rgba(0,242,255,0.6)]">®</sup>
            </span>
          </Link>
          <div className="hidden sm:flex items-center gap-1 md:gap-2">
            <Button variant="link" size="sm" asChild><Link to="/pricing">Pricing</Link></Button>
            <Link
              to="/spot-the-pattern"
              className="hidden sm:inline-flex whitespace-nowrap px-3 py-1.5 text-xs md:px-2 md:text-sm lg:px-3 transition-colors text-cyan-300/90 hover:text-cyan-200"
            >
              <span className="md:hidden">Free Gaslighting Quiz</span>
              <span className="hidden md:inline">Free Daily Gaslighting Quiz</span>
            </Link>
            <Link
              to="/resources"
              className="px-3 py-1.5 text-xs md:px-2 md:text-sm lg:px-3 text-white/70 hover:text-cyan-300 transition-colors"
            >
              Resources
            </Link>
            <Link
              to="/professionals"
              className="hidden md:inline px-3 py-1.5 text-xs md:px-2 md:text-sm lg:px-3 text-white/70 hover:text-cyan-300 transition-colors"
            >
              For Professionals
            </Link>
            <button
              onClick={goToSignin}
              className="px-3 py-1.5 text-xs md:px-2 md:text-sm lg:px-3 text-white/70 hover:text-cyan-300 transition-colors"
            >
              Sign in
            </button>
          </div>
          <button
            className="sm:hidden -mr-2 p-2 rounded-lg text-white/70 hover:text-cyan-300 transition-colors touch-manipulation"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            style={{ minWidth: "44px", minHeight: "44px" }}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {menuOpen && (
          <div
            id="mobile-menu"
            className="sm:hidden border-t border-cyan-400/10 bg-black/85 backdrop-blur-md"
          >
            <div className="max-w-6xl mx-auto px-5 py-1 flex flex-col">
              <Link
                to="/spot-the-pattern"
                onClick={() => setMenuOpen(false)}
                className="py-3.5 text-sm text-cyan-300 border-b border-white/[0.06] touch-manipulation"
              >
                Free Daily Gaslighting Quiz
              </Link>
              <Link
                to="/pricing"
                onClick={() => setMenuOpen(false)}
                className="py-3.5 text-sm text-white/75 border-b border-white/[0.06] touch-manipulation"
              >
                Pricing
              </Link>
              <Link
                to="/resources"
                onClick={() => setMenuOpen(false)}
                className="py-3.5 text-sm text-white/75 border-b border-white/[0.06] touch-manipulation"
              >
                Resources
              </Link>
              <Link
                to="/professionals"
                onClick={() => setMenuOpen(false)}
                className="py-3.5 text-sm text-white/75 border-b border-white/[0.06] touch-manipulation"
              >
                For Professionals
              </Link>
              <button
                onClick={goToSignin}
                className="py-3.5 text-left text-sm text-white/75 hover:text-cyan-300 transition-colors touch-manipulation"
              >
                Sign in
              </button>
            </div>
          </div>
        )}
      </nav>

      <main>
      {/* HERO — analyzer-led */}
      <header className="relative flex items-center justify-center overflow-hidden">
        <div
          aria-hidden
          className="absolute -top-20 left-1/2 -translate-x-1/2 w-[140vw] md:w-[1100px] h-[600px] rounded-full opacity-70 animate-driftSlow"
          style={{
            background:
              "radial-gradient(closest-side, rgba(34,211,238,0.18), rgba(0,0,0,0) 70%)",
            filter: "blur(40px)",
          }}
        />
        <div
          aria-hidden
          className="absolute bottom-[-180px] right-[-120px] w-[560px] h-[560px] rounded-full opacity-50 animate-driftSlow [animation-delay:-6s]"
          style={{
            background:
              "radial-gradient(closest-side, rgba(8,145,178,0.35), rgba(0,0,0,0) 70%)",
            filter: "blur(50px)",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.5]"
          style={{
            background:
              "repeating-linear-gradient(180deg, rgba(255,255,255,0.018) 0px, rgba(255,255,255,0.018) 1px, transparent 1px, transparent 4px)",
          }}
        />

        <div className="relative z-10 w-full max-w-3xl mx-auto px-5 pt-5 pb-6 md:pt-8 md:pb-10 text-center">
          <h1
            className="text-[30px] leading-[1.06] md:text-6xl md:leading-[1.04] font-semibold tracking-tight animate-fadeUpSoft"
            style={{ textShadow: "0 0 50px rgba(34,211,238,0.18)" }}
          >
            Finally name what you <span className="text-cyan-300">couldn't explain.</span>
          </h1>

          <p className="mt-2.5 md:mt-3 text-[14px] md:text-base text-white/60 leading-relaxed max-w-xl mx-auto animate-fadeUpSoft [animation-delay:120ms]">
            Paste any message. See the pattern — named, in seconds.
          </p>
          <p className="mt-1.5 text-[12px] md:text-[13px] text-white/40 font-mono tracking-wide whitespace-nowrap animate-fadeUpSoft [animation-delay:160ms]">
            Three free analyses · No signup
          </p>

          <div id="hero-analyzer" className="mt-4 md:mt-5 animate-fadeUpSoft [animation-delay:220ms] scroll-mt-24">
            <PasteAnalyzer size="lg" />
          </div>

          <p className="mt-4 text-[15px] md:text-[17px] font-semibold tracking-tight text-cyan-300 leading-relaxed drop-shadow-[0_0_14px_rgba(34,211,238,0.35)] animate-fadeUpSoft [animation-delay:260ms]">
            Then practice your reply: three turns, free.
          </p>


          {/* FEATURED ON — directory badges. Homepage only; add more badges inside the row below. */}
          <div className="mt-7 md:mt-9 flex flex-col items-center">
            <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40">
              FEATURED ON
            </span>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-x-6 gap-y-4" onClick={(event) => {
              const anchor = event.target instanceof Element ? event.target.closest("a") : null;
              if (anchor?.hostname === "theresanaiforthat.com") trackDirectoryBadgeClick("taaft");
              else if (anchor?.hostname === "www.toolpilot.ai") trackDirectoryBadgeClick("toolpilot");
            }} onAuxClick={(event) => {
              if (event.button !== 1) return;
              const anchor = event.target instanceof Element ? event.target.closest("a") : null;
              if (anchor?.hostname === "theresanaiforthat.com") trackDirectoryBadgeClick("taaft");
              else if (anchor?.hostname === "www.toolpilot.ai") trackDirectoryBadgeClick("toolpilot");
            }}>
              <a
                href="https://theresanaiforthat.com/ai/greyrockai-app/?ref=featured&v=13634524"
                target="_blank"
                rel="nofollow"
              >
                <img width={300} src="https://media.theresanaiforthat.com/featured-on-taaft.png?width=600" />
              </a>
              <a
                href="https://www.toolpilot.ai"
                target="_blank"
                rel="noopener"
              >
                {/* Height matched to the TAAFT badge (63px at width 300) so both badges sit evenly side by side. */}
                <img width={286} height={63} src={toolpilotBadge} alt="Featured on ToolPilot" />
              </a>
            </div>
          </div>

        </div>
      </header>

      {/* LIVED RECOGNITION — calm centered prose */}
      <Section eyebrow="You're not imagining it" className="py-10 md:py-14">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl md:text-4xl font-semibold tracking-tight leading-[1.1]">
            You re-read it five times —{" "}
            <span className="block mt-1 text-cyan-300">and still can't tell if you're overreacting.</span>
          </h2>
          <p className="mt-4 text-[13px] md:text-[14px] text-white/55 leading-relaxed max-w-xl mx-auto">
            You walk in with a clear point and walk out apologizing. You feel the knot in your stomach before you can put words to why. That feeling is real — and what caused it has a name.
          </p>
        </div>
      </Section>

      {/* HOW IT WORKS — three steps, no jargon */}
      <Section eyebrow="How it works" className="py-10 md:py-14">
        <h2 className="text-2xl md:text-4xl font-semibold tracking-tight leading-[1.1] mb-6 max-w-2xl">
          Three steps. <span className="text-white/45">No jargon.</span>
        </h2>
        <div className="grid md:grid-cols-3 gap-3 md:gap-4">
          {[
            {
              icon: Upload,
              title: "Paste",
              desc: "Drop in a text, DM, or conversation — anything that felt off.",
            },
            {
              icon: Brain,
              title: "See what happened",
              desc: "It names the tactic in plain language and shows you what was really going on.",
            },
            {
              icon: Sparkles,
              title: "Know what to say",
              desc: "Get a calibrated reply — or practice the conversation before you have it.",
            },
          ].map((s, i) => (
            <div key={s.title} className="relative rounded-xl border border-white/[0.07] bg-white/[0.02] p-4 md:p-5 hover:border-cyan-400/40 transition-all">
              <span className="absolute top-3 right-4 text-[10px] font-mono text-cyan-300/70">
                0{i + 1}
              </span>
              <div className="w-9 h-9 rounded-md bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center mb-3 shadow-[0_0_20px_-8px_rgba(34,211,238,0.7)]">
                <s.icon className="w-4 h-4 text-cyan-300" />
              </div>
              <h3 className="text-[15px] font-semibold tracking-tight mb-1 uppercase">{s.title}</h3>
              <p className="text-[13px] text-white/55 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* PATTERNS — what it names */}
      <Section eyebrow="What it names" className="py-10 md:py-12">
        <div className="mb-6 md:mb-7 max-w-2xl">
          <h2 className="text-2xl md:text-4xl font-semibold tracking-tight leading-[1.1]">
            It puts words to <span className="text-white/45">what just happened.</span>
          </h2>
          <p className="mt-3 text-white/55 text-[13px] md:text-[14px] leading-relaxed max-w-xl">
            The moments hardest to explain are the ones it recognizes fastest. Whatever you paste, it names what's there — in plain language, so you can stop second-guessing yourself.
          </p>
        </div>


        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
          {PATTERNS.map((p) => (
            <div
              key={p.name}
              className="group relative rounded-lg border border-white/[0.06] bg-gradient-to-b from-white/[0.025] to-transparent p-3 md:p-4 hover:border-cyan-400/60 hover:bg-cyan-400/[0.04] hover:shadow-[0_0_28px_-4px_rgba(34,211,238,0.45),inset_0_0_18px_-6px_rgba(34,211,238,0.25)] transition-all duration-500 overflow-hidden"
            >
              <div className="flex items-center justify-end mb-2">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400/70 group-hover:bg-cyan-300 group-hover:shadow-[0_0_18px_rgba(34,211,238,1)] transition-all" />
              </div>
              <h3 className="text-[13px] md:text-sm font-semibold tracking-tight mb-1 group-hover:text-cyan-200 transition-colors uppercase">
                {p.name}
              </h3>
              <p className="text-[12px] md:text-[13px] leading-snug text-white/55">{p.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ENGINE BAND — reassurance: real analysis, not a vibe check */}
      <Section eyebrow="Under the hood" className="py-10 md:py-14">
        <div className="max-w-2xl">
          <h2 className="text-2xl md:text-4xl font-semibold tracking-tight leading-[1.1]">
            This isn't a vibe check.{" "}
            <span className="text-white/45">It's a real analysis.</span>
          </h2>
          <p className="mt-4 text-[13px] md:text-[14px] text-white/55 leading-relaxed">
            Behind the plain language is a forensic engine that reads tone, tracks how pressure builds across a whole conversation, and weighs the words against patterns documented in abuse research — the kind of analysis used in forensic settings, made readable for the person living it.
          </p>
          <p className="mt-3 text-[11px] md:text-[12px] text-white/40 leading-relaxed">
            Built on the pattern-recognition principles behind{" "}
            <Link to="/articulation-before-adjudication" className="underline underline-offset-2 decoration-cyan-400/30 hover:text-cyan-300 hover:decoration-cyan-400/60 transition-colors">
              emerging forensic research
            </Link>{" "}
            into coercive control and manipulation.
          </p>
        </div>

        {/* Proof chips */}
        <div className="mt-6 flex flex-wrap gap-2">
          {[
            "Reads the whole conversation, not just one line",
            "Names the specific tactic",
            "Rates how serious it is",
            "Tells you what to say back",
          ].map((chip) => (
            <span
              key={chip}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] text-white/70 border border-white/[0.07] bg-white/[0.02]"
            >
              <Check className="w-3 h-3 text-cyan-300/80" />
              {chip}
            </span>
          ))}
        </div>

        {/* Before / after visual */}
        <div className="mt-8 grid md:grid-cols-2 gap-3 md:gap-4">
          <div className="rounded-xl border border-white/[0.07] bg-gradient-to-b from-white/[0.04] to-transparent p-4 md:p-5">
            <span className="text-[10px] uppercase tracking-[0.25em] font-mono text-white/40">
              The message
            </span>
            <p className="mt-3 text-[14px] md:text-[15px] text-white/80 leading-relaxed italic">
              "If you really cared, you wouldn't be asking."
            </p>
          </div>
          <div className="rounded-xl border border-cyan-400/20 bg-gradient-to-b from-white/[0.04] to-transparent p-4 md:p-5">
            <span className="text-[10px] uppercase tracking-[0.25em] font-mono text-white/40">
              What it is
            </span>
            <p className="mt-2 text-[14px] md:text-[15px] font-semibold text-cyan-200 leading-snug">
              Blame Redirection
            </p>
            <p className="mt-1 text-[13px] text-white/55 leading-relaxed">
              Your question gets turned into proof that you don't trust them — so the original concern disappears.
            </p>
          </div>
        </div>
      </Section>


      {/* FINAL CTA — analyzer repeat */}
      <section
        id="final-cta"
        className="relative px-5 py-8 md:py-16 text-center overflow-hidden"
      >
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(60% 40% at 50% 50%, rgba(34,211,238,0.18) 0%, rgba(0,0,0,0) 70%)",
          }}
        />
        <div
          aria-hidden
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full animate-pulseGlow pointer-events-none"
          style={{
            background:
              "radial-gradient(closest-side, rgba(34,211,238,0.16), rgba(0,0,0,0) 70%)",
            filter: "blur(40px)",
          }}
        />

        <div className="relative max-w-2xl mx-auto px-1">
          <h2 className="text-[1.35rem] md:text-[2.6rem] font-semibold tracking-tight leading-[1.15] md:leading-[1.1]">
            The pattern is already there.{" "}
            <span className="block mt-1 md:mt-2 text-cyan-300">
              The question is whether you'll see it before it costs you.
            </span>
          </h2>
          <div className="mt-4 md:mt-7">
            <a
              href="#hero-analyzer"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("hero-analyzer")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="inline-flex items-center gap-1.5 text-[13px] text-cyan-300/80 hover:text-cyan-200 transition-colors"
            >
              ↑ Try it now
            </a>
          </div>
        </div>
      </section>
      </main>



      <div className="border-t border-white/[0.05] pt-8 pb-4 px-5">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-white/35 mb-5">
            Resources
          </p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-3">
            {[
              { to: "/free-gaslighting-detector", label: "Free gaslighting detector →" },
              { to: "/why-do-i-feel-exhausted-after-conversations", label: "Why these conversations exhaust you →" },
              { to: "/manipulative-text-message-analysis", label: "Manipulative text message analysis →" },
              { to: "/chatgpt-vs-greyrock-ai", label: "ChatGPT vs GreyRock AI →" },
              { to: "/darvo-examples", label: "DARVO examples →" },
              { to: "/reactive-abuse-examples", label: "Reactive abuse examples →" },
              { to: "/emotional-invalidation-examples", label: "Emotional invalidation examples →" },
              { to: "/grey-rock-method-examples", label: "Grey rock method examples →" },
              { to: "/ai-detect-narcissistic-abuse", label: "How AI detects narcissistic abuse →" },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-[12px] font-mono uppercase tracking-[0.18em] text-cyan-300/70 hover:text-cyan-200 transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <footer className="border-t border-cyan-400/10 py-6 px-5 text-center text-[11px] font-mono text-white/60 tracking-wider">
        <div>GREYROCKAI® · BEHAVIORAL INTELLIGENCE</div>
        <div className="mt-1.5">
          A{" "}
          <a href="https://shouldistudios.com" target="_blank" rel="noopener" className="hover:text-cyan-300/80 transition-colors">
            Should I Studios
          </a>{" "}
          product
        </div>
        <div className="mt-1.5 flex items-center justify-center gap-4">
          <Link to="/privacy" className="hover:text-cyan-300/80 transition-colors">Privacy</Link>
          <span>·</span>
          <Link to="/professionals" className="hover:text-cyan-300/80 transition-colors">For Professionals</Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-cyan-300/80 transition-colors">Terms</Link>
          <span>·</span>
          <Link to="/contact" className="hover:text-cyan-300/80 transition-colors">Contact</Link>
          <span>·</span>
          <Link to="/spot-the-pattern" className="hover:text-cyan-300/80 transition-colors">Free Daily Gaslighting Quiz</Link>
        </div>
      </footer>

      <style>{`
        @keyframes fadeUpSoft {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes floatLabel {
          0%, 100% { opacity: 0; transform: translateY(8px); }
          15%, 70% { opacity: 0.85; transform: translateY(0); }
          85% { opacity: 0; transform: translateY(-6px); }
        }
        @keyframes driftSlow {
          0%, 100% { transform: translate(-50%, 0) scale(1); }
          50% { transform: translate(-50%, 25px) scale(1.05); }
        }
        @keyframes pulseGlow {
          0%, 100% { opacity: 0.55; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.9; transform: translate(-50%, -50%) scale(1.08); }
        }
        @keyframes cyanPulse {
          0%, 100% { box-shadow: 0 0 40px -6px rgba(34,211,238,0.7); }
          50% { box-shadow: 0 0 60px -2px rgba(34,211,238,1); }
        }
        @keyframes blink {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
        @keyframes scanline {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
        @keyframes tickerSlide {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .cinematic-root .animate-fadeUpSoft {
          animation: fadeUpSoft 0.7s cubic-bezier(0.2, 0.7, 0.2, 1) both;
        }
        .cinematic-root .animate-floatLabel {
          animation: floatLabel 7s ease-in-out infinite both;
        }
        .cinematic-root .animate-driftSlow {
          animation: driftSlow 14s ease-in-out infinite;
        }
        .cinematic-root .animate-pulseGlow {
          animation: pulseGlow 6s ease-in-out infinite;
        }
        .cinematic-root .animate-cyanPulse {
          animation: cyanPulse 3.2s ease-in-out infinite;
        }
        .cinematic-root .animate-blink {
          animation: blink 1s steps(1) infinite;
        }
        .cinematic-root .animate-tickerSlide {
          animation: tickerSlide 32s linear infinite;
        }
        .cinematic-root .cinematic-scanline {
          background: linear-gradient(180deg, transparent 0%, rgba(34,211,238,0.18) 50%, transparent 100%);
          animation: scanline 2.6s linear infinite;
        }
        .cinematic-root .mask-fade-x {
          -webkit-mask-image: linear-gradient(90deg, transparent 0%, #000 12%, #000 88%, transparent 100%);
          mask-image: linear-gradient(90deg, transparent 0%, #000 12%, #000 88%, transparent 100%);
        }
      `}</style>
    </div>
  );
};

export default CinematicLanding;
